import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { CtDimensionsService } from '@cloud-twin-v2/cloud-twin-shared/dimensions/ct-dimensions.service';
import { CtNetworkElementTypesService } from '@cloud-twin-v2/cloud-twin-shared/network-element-types/ct-network-element-types.service';
import { CtWorkspaceDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { IntegrableForm, IntegrableFormParams } from '@common-modules/shared/forms/integrable-form';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { ObjectHelperService } from '@common-modules/shared/helpers/object-helper.service';
import { LocalizationHelperService } from '@common-modules/shared/localization/localization-helper.service';
import { SelectOption } from '@common-modules/shared/model/shared/select-option';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import {
  Observable,
  Subject,
  Subscription,
  combineLatest,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { TelemetryFilters } from '../models/telemetry-filters';

const COMPONENT_SELECTOR = 'ct-measurement-filters';

@UntilDestroy()
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './measurement-filters.component.html',
  styleUrl: './measurement-filters.component.scss',
  standalone: false,
})
export class MeasurementFiltersComponent extends IntegrableForm {
  private _workspace: CtWorkspaceDto;

  public get workspace(): CtWorkspaceDto {
    return this._workspace;
  }
  @Input() set workspace(value: CtWorkspaceDto) {
    this._workspace = value;
    if (this.workspace) {
      this.buildFields();

      this.initialLoad();
    }
  }

  @Input() initialModel: TelemetryFilters;

  private _clearAll$ = new Subject<void>();
  get clearAll$() {
    return this._clearAll$;
  }
  @Input() set clearAll$(value) {
    if (value) {
      this._clearAllSubscription?.unsubscribe();

      this._clearAllSubscription = value.pipe(untilDestroyed(this)).subscribe(() => {
        this.clearAllFilters();
      });
    }
  }

  @Output() applyFilters = new EventEmitter<TelemetryFilters>();

  model: TelemetryFilters;
  fieldConfig: FormlyFieldConfig[];
  formOptions: FormlyFormOptions = {};
  readonly T_SCOPE = `${TranslateScopes.Telemetry}.${COMPONENT_SELECTOR}`;
  private _clearAllSubscription: Subscription | null = null;
  private readonly _dimensionsService = inject(CtDimensionsService);
  private readonly _neTypesService = inject(CtNetworkElementTypesService);
  private readonly _localization = inject(LocalizationHelperService);
  private readonly _objectHelper = inject(ObjectHelperService);

  initialLoad(): void {
    const model = this.getInitialModel();
    this.applyFilters.emit(model);
  }

  onModelChange(model: TelemetryFilters): void {
    const areEqual = this._objectHelper.deepEqual(model, this.model);

    if (areEqual || !model.modelId || !model.modelVersionId) {
      return;
    }

    this.model = globalUtilsHelper.clone(model, true);
  }

  onIsValid(isValid: boolean): void {
    this.setIsValid(isValid);
  }

  onHasChanges(hasChanges: boolean): void {
    this.setHasChanges(hasChanges);
  }

  getModel() {
    return this.model;
  }

  setParams(params: IntegrableFormParams): void {
    this.formOptions.formState.op = params.op;
    this.formOptions = globalUtilsHelper.clone(this.formOptions);
  }

  setInitialModel(model: any): void {
    this.initialModel = globalUtilsHelper.clone(model, true);
  }

  getModelOptions(): SelectOption<string>[] {
    const uniqueOptions = {};

    this.workspace.workspaceConnectSettings.forEach((item) => {
      uniqueOptions[item.instanceId] = new SelectOption({
        value: item.instanceId,
        label: item.instanceName,
      });
    });

    return Object.values(uniqueOptions);
  }

  getInitialModel() {
    const initialModel = {} as TelemetryFilters;
    initialModel.modelId = this.workspace.workspaceConnectSettings[0]?.instanceId;
    initialModel.modelVersionId = this.workspace.workspaceConnectSettings[0]?.snapshotId;

    return initialModel;
  }

  onClickApply() {
    this.applyFilters.emit(this.model);
  }

  clearAllFilters(): void {
    this.model = this.getInitialModel();
    this.formOptions.resetModel({});
    this.buildFields();
    this.applyFilters.emit(this.model);
  }

  private getModelIdCtrl = (field: FormlyFieldConfig<any>) =>
    field.parent.get('modelId').formControl;

  private getModelVersionIdCtrl = (field: FormlyFieldConfig<any>) =>
    field.parent.get('modelVersionId').formControl;

  private getDimensionIdCtrl = (field: FormlyFieldConfig<any>) =>
    field.parent.get('dimensionId').formControl;

  private getModelVersionsByModelId(modelId: string) {
    if (!modelId) {
      return [];
    }

    const instanceList = this.workspace.workspaceConnectSettings.filter(
      (instances) => instances.instanceId === modelId,
    );

    const uniqueInstances = Array.from(
      new Map(instanceList.map((item) => [item.snapshotId, item])).values(),
    );

    return uniqueInstances.map((m) => ({
      value: m.snapshotId,
      label: m.snapshotName,
    }));
  }

  private getDimensionsByModelVersionId(
    modelVersionId: string,
  ): Observable<SelectOption<string>[]> {
    if (!modelVersionId) {
      return of([]);
    }

    const instanceList = this.workspace.workspaceConnectSettings.filter(
      (instances) => instances.snapshotId === modelVersionId,
    );

    const uniqueScenarios = Array.from(
      new Map(instanceList.map((item) => [item.scenarioId, item.scenarioId])).values(),
    );

    return this._dimensionsService.getDimensionsOptions(uniqueScenarios);
  }

  private getUniqueScenarios(modelVersionId: string): string[] {
    if (!modelVersionId) {
      return [];
    }

    const instanceList = this.workspace.workspaceConnectSettings.filter(
      (instances) => instances.snapshotId === modelVersionId,
    );

    return Array.from(
      new Map(instanceList.map((item) => [item.scenarioId, item.scenarioId])).values(),
    );
  }

  private buildFields(): void {
    this._localization.get(this.T_SCOPE).subscribe((translations) => {
      this.fieldConfig = [
        {
          fieldGroupClassName: 'row',
          fieldGroup: [
            {
              key: 'modelId',
              type: 'select',
              className: 'col-3',
              props: {
                label: translations['model'],
              },
              hooks: {
                onInit: (field) => {
                  const options = this.getModelOptions();
                  field.props.options = options;

                  if (options && options.length > 0) {
                    field.formControl.setValue(options[0].value);
                  }
                },
              },
            },
            {
              key: 'modelVersionId',
              type: 'select',
              className: 'col-3',
              props: {
                label: translations['model-version'],
                type: 'select',
              },
              hooks: {
                onInit: (field) => {
                  const modelIdCtrl = this.getModelIdCtrl(field);

                  field.props.options = modelIdCtrl.valueChanges.pipe(
                    startWith(modelIdCtrl.value),
                    map((modelId) => {
                      if (!modelId) return [];

                      const modelVersions = this.getModelVersionsByModelId(modelId);

                      if (modelVersions && modelVersions.length > 0) {
                        field.formControl.setValue(modelVersions[0].value);
                      } else {
                        field.formControl.setValue(undefined);
                      }

                      return modelVersions;
                    }),
                  );
                },
              },
            },
            {
              key: 'dimensionId',
              type: 'select',
              className: 'col-3',
              props: {
                label: translations['dimension'],
                type: 'select',
              },
              hooks: {
                onInit: (field) => {
                  const modelVersionIdCtrl = this.getModelVersionIdCtrl(field);

                  field.props.options = modelVersionIdCtrl.valueChanges.pipe(
                    startWith(modelVersionIdCtrl.value),
                    switchMap((modelVersionId: string) => {
                      if (!modelVersionId) return of([]);

                      return this.getDimensionsByModelVersionId(modelVersionId).pipe(
                        tap((dimensions) => {
                          if (dimensions && dimensions.length === 1) {
                            field.formControl.setValue(dimensions[0].value);
                          } else {
                            field.formControl.setValue(undefined);
                          }
                        }),
                      );
                    }),
                  );
                },
              },
            },
            {
              key: 'networkElementTypeId',
              type: 'select',
              className: 'col-3',
              props: {
                label: translations['ne-types'],
                type: 'select',
              },
              hooks: {
                onInit: (field) => {
                  const modelVersionIdCtrl = this.getModelVersionIdCtrl(field);
                  const dimensionIdCtrl = this.getDimensionIdCtrl(field);

                  field.props.options = combineLatest([
                    modelVersionIdCtrl.valueChanges.pipe(startWith(modelVersionIdCtrl.value)),
                    dimensionIdCtrl.valueChanges.pipe(startWith(dimensionIdCtrl.value)),
                  ]).pipe(
                    switchMap(([modelVersionId, dimensionId]) => {
                      if (!modelVersionId || !dimensionId) {
                        return of([]);
                      }

                      const uniqueScenarios = this.getUniqueScenarios(modelVersionId);

                      return this._neTypesService
                        .getNetworkElementTypesOptions(uniqueScenarios, dimensionId)
                        .pipe(
                          tap((options) => {
                            if (options && options.length === 1) {
                              field.formControl.setValue(options[0].value);
                            } else {
                              field.formControl.setValue(undefined);
                            }
                          }),
                        );
                    }),
                  );
                },
              },
            },
          ],
        },
      ];
    });
  }
}
